import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontWeights,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, Heading, HtmlContent, Svg } from './ui';
import linkedinIcon from '../images/linkedin-icon.inline.svg';

const StyledTeamProfiles = styled.section`
  ${sectionMargins('45px', '90px')};
`;

const StyledHeading = styled(Heading)`
  text-align: center;
`;

const StyledText = styled(HtmlContent)`
  margin-top: 20px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}
`;

const StyledFilters = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
  `}
`;

const StyledFilter = styled.button`
  font-weight: ${fontWeights.regular};
  background-color: ${standardColours.transparent};
  border: solid 2px ${brandColours.secondary};
  padding: 15px 24px;
  border-radius: 32px;
  transition: ${standardTransition('color')},
    ${standardTransition('background-color')};

  ${({ active }) => {
    if (active) {
      return css`
        background-color: ${brandColours.secondary};
        color: ${standardColours.white};
      `;
    }
  }}
`;

const StyledItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;

  ${minBreakpointQuery.small`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.smedium`
    margin-top: 50px;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 60px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 70px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-top: 80px;
  `}
`;

// moved up for hover
const StyledProfileIllustration = styled.img`
  height: 100%;
  width: 100%;
  transition: ${standardTransition('opacity')};
`;

// moved up for hover
const StyledProfilePicture = styled(GatsbyImage)`
  height: 100%;
  width: 100%;
  max-height: 50px;
  position: absolute;
  border-radius: 50%;
  bottom: 0;
  left: 0;
  opacity: 0;
  transition: ${standardTransition('opacity')};

  ${minBreakpointQuery.small`
    max-height: 75px;
  `}

  ${minBreakpointQuery.large`
    max-height: 100px;
  `}
`;

// moved up for hover
const StyledBiography = styled(HtmlContent)`
  grid-area: 1/1;
`;

// moved up for hover
const StyledFacts = styled.div`
  grid-area: 1/1;
`;

const StyledItem = styled.article`
  --columns: 1;
  width: calc((100% - (var(--columns) - 1) * 20px) / var(--columns));
  background-color: ${standardColours.white};
  border-radius: 20px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  transition: ${standardTransition('color')},
    ${standardTransition('background-color')};

  ${minBreakpointQuery.small`
    padding: 40px;
  `}

  ${minBreakpointQuery.smedium`
    --columns: 2;
  `}

  ${minBreakpointQuery.large`
    padding: 50px;
  `}

  &:hover {
    background-color: ${brandColours.quaternary};
    color: white;

    ${StyledProfileIllustration} {
      opacity: 0;
    }

    ${StyledProfilePicture} {
      opacity: 1;
    }

    ${StyledBiography} {
      ${visuallyHidden()}
    }
  }

  &:not(:hover) {
    ${StyledFacts} {
      ${visuallyHidden()}
    }
  }
`;

const StyledTop = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;

  ${minBreakpointQuery.small`
    gap: 20px;
  `}

  ${minBreakpointQuery.large`
    gap: 30px;
  `}
`;

const StyledProfile = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 50px;
  max-height: 54px;
  flex-shrink: 0;

  ${minBreakpointQuery.small`
    max-width: 75px;
    max-height: 77px;
  `}

  ${minBreakpointQuery.large`
    max-width: 100px;
    max-height: 108px;
  `}
`;

const StyledIconLink = styled.a`
  background-color: #0077b5;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  padding: 5px;
  z-index: 1;
  transition: ${standardTransition('background-color')};
  bottom: 0;
  left: 0;
  position: absolute;

  ${minBreakpointQuery.large`
    width: 40px;
    height: 40px;
    padding: 10px;
  `}

  &:hover {
    background-color: ${brandColours.tertiary};
  }
`;

const StyledLinkedinIcon = styled(Svg)`
  width: 100%;
  height: 100%;
  fill: ${standardColours.white};
`;

const StyledName = styled.h3`
  margin-bottom: 5px;
`;

const StyledRole = styled.p``;

const StyledBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
`;

const StyledBadge = styled.img`
  width: 100%;
  height: 100%;
  max-width: 30px;
  max-height: 30px;

  ${minBreakpointQuery.small`
    max-width: 40px;
    max-height: 40px;
  `}

  ${minBreakpointQuery.large`
    max-width: 50px;
    max-height: 50px;
  `}
`;

const StyledContent = styled.div`
  display: grid;
`;

const StyledFactsContent = styled(HtmlContent)`
  margin-top: 20px;

  ul {
    li {
      &:before {
        background-color: ${standardColours.white};
      }
    }
  }
`;

const TeamProfiles = ({ heading, text }) => {
  const {
    allDatoCmsTeam: { nodes },
  } = useStaticQuery(graphql`
    query TeamProfilesQuery {
      allDatoCmsTeam(sort: { position: ASC }) {
        nodes {
          name
          role
          department {
            title
          }
          profileIllustration {
            url
            alt
          }
          profilePicture {
            gatsbyImageData(width: 100, height: 100)
            alt
          }
          badges {
            url
            alt
          }
          biography {
            value
            links {
              id: originalId
              text
              pageUrl {
                ...LinkFragment
              }
            }
          }
          funFacts {
            value
          }
          linkedinLink {
            url
          }
        }
      }
    }
  `);

  const initialFilters = [];

  nodes.forEach(({ department }) => {
    initialFilters.push(department.title);
  });

  const departmentFilters = [...new Set(initialFilters)];

  const getProfiles = filterTitle => {
    if (filterTitle) {
      return filterTitle === 'All'
        ? nodes
        : nodes.filter(nodes => nodes.department.title === filterTitle);
    }
    return nodes;
  };

  const [activeFilter, setActiveFilter] = useState('All');

  const handleButtonClick = filterTitle => {
    setActiveFilter(prevTitle =>
      prevTitle === filterTitle ? 'All' : filterTitle,
    );
  };

  return (
    <StyledTeamProfiles>
      <Container narrow={true}>
        <StyledHeading>{heading}</StyledHeading>
        {text && <StyledText content={text} />}
        <StyledFilters>
          <StyledFilter
            active={activeFilter === 'All'}
            onClick={() => {
              setActiveFilter('All');
            }}
          >
            All
          </StyledFilter>
          {departmentFilters.map((item, id) => (
            <StyledFilter
              key={id}
              active={activeFilter === item}
              onClick={() => {
                handleButtonClick(item);
              }}
            >
              {item}
            </StyledFilter>
          ))}
        </StyledFilters>
        <StyledItems>
          {getProfiles(activeFilter).map(
            (
              {
                name,
                role,
                profilePicture,
                profileIllustration,
                badges,
                biography,
                funFacts,
                linkedinLink,
              },
              id,
            ) => (
              <StyledItem>
                <StyledTop>
                  <StyledProfile>
                    {linkedinLink && (
                      <StyledIconLink
                        href={linkedinLink.url}
                        target="_blank"
                        rel="noopener"
                        aria-label={`${name} Linkedin Link`}
                      >
                        <StyledLinkedinIcon image={linkedinIcon} />
                      </StyledIconLink>
                    )}
                    <StyledProfileIllustration
                      src={profileIllustration.url}
                      alt={profileIllustration.alt}
                      loading="lazy"
                    />
                    <StyledProfilePicture
                      image={profilePicture.gatsbyImageData}
                      alt={profilePicture.alt}
                    />
                  </StyledProfile>
                  <div>
                    <StyledName>{name}</StyledName>
                    <StyledRole>{role}</StyledRole>
                    <StyledBadges>
                      {badges.map(({ url, alt }, id) => (
                        <StyledBadge
                          key={id}
                          src={url}
                          alt={alt}
                          loading="lazy"
                        />
                      ))}
                    </StyledBadges>
                  </div>
                </StyledTop>
                <StyledContent>
                  <StyledBiography content={biography} />
                  <StyledFacts>
                    <span>
                      <strong>Fun Facts:</strong>
                    </span>
                    <StyledFactsContent content={funFacts} />
                  </StyledFacts>
                </StyledContent>
              </StyledItem>
            ),
          )}
        </StyledItems>
      </Container>
    </StyledTeamProfiles>
  );
};

export default TeamProfiles;
